import { getDragAndDropAttributes } from '@components/ArtBoard/DragAndDrop/getDragAndDropAttributes'
import useOnDragStart from '@components/ArtBoard/DragAndDrop/hooks/useOnDragStart'
import {
  DRAGGABLE_TYPE,
  DROPZONE_TYPE,
} from '@components/ArtBoard/DragAndDrop/types'
import { NodePreviewWrapper } from '@components/ArtBoard/NodePreviewWrapper'
import type { useClickOutside } from '@lib/hooks'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { useRef } from 'react'
import { useSelector } from 'react-redux'

import { TREE_MATRIX } from '@/constants/constants'
import type { TreeItemVariants } from '@/types'
import { Children } from '../../../Children'
import { ChildrenCorner } from '../../../ChildrenCorner'
import { Name } from '../../../Name'
import { ParentCorners } from '../../../ParentCorners'
import css from './NodeSimpleView.module.scss'

type Props = {
  id: string
  name: string
  variant: TreeItemVariants
  isChild: boolean
  hasChild: boolean
  hasChildren: boolean
  isCollapsed: boolean
  parentId: string | null
  nodeStyles: React.CSSProperties
  canDrag: boolean
  isEditable: boolean
  isSelected: boolean
  isOver: boolean
  isDragging: boolean
  isOverlay: boolean
  creationDisabled: boolean
  clickOutside: ReturnType<typeof useClickOutside<HTMLDivElement>>
}

const NodeSimpleView: React.FC<Props> = (props) => {
  const ref = useRef<HTMLDivElement>(null)

  const isProjectMobile = useSelector(
    migrationProjectSelector.getIsProjectMobile
  )

  const projectTreeMode = useSelector(
    migrationProjectSelector.getProjectTreeType
  )

  // Переименование и перенос страниц работают так же, как в обычном режиме
  // (OCT-5138): те же хуки DnD и тот же Name — карточка лишь нарисована
  // компактно. Выбор ноды приходит из useOnDragStart, он же открывает
  // редактирование по клику на уже выбранной ноде.
  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.PAGE, props.id, {})

  const dragAttributes =
    props.canDrag && !props.isOverlay ? { onPointerDown: onDragStart } : {}

  const dropAttributes = getDragAndDropAttributes(
    props.id,
    DROPZONE_TYPE.PAGE,
    { nodeId: props.id },
    props.isOverlay
  )

  const nodeClasses = {
    container: cx('Tree-node', css.container, {
      [css.dragging]: props.isDragging,
      [css.matrix]: projectTreeMode === TREE_MATRIX,
    }),
    node: cx(css.node, {
      [css.mobile]: isProjectMobile,
      [css.selected]: props.isSelected,
      [css.over]: props.isOver,
    }),
  }

  return (
    <div
      className={nodeClasses.container}
      style={props.nodeStyles}
      ref={ref}
      {...dropAttributes}
    >
      {props.isChild && projectTreeMode !== TREE_MATRIX && (
        <ChildrenCorner id={props.id} parentId={props.parentId} />
      )}

      {(props.hasChildren || props.hasChild) &&
        projectTreeMode !== TREE_MATRIX &&
        !props.isCollapsed && <ParentCorners />}

      {/* Без ref из useClickOutside любой mousedown, в том числе по самой
          карточке, считается кликом снаружи и снимает выделение — выбрать
          страницу и переименовать её становится нельзя. */}
      <div
        id={props.id}
        className={nodeClasses.node}
        {...(props.isSelected ? props.clickOutside : {})}
      >
        <div
          {...dragAttributes}
          onDragStart={(event) => event.preventDefault()}
        >
          <Name
            id={props.id}
            name={props.name}
            isEditable={props.isEditable}
            variant={props.variant}
            className={css.name}
          />
        </div>

        {!props.creationDisabled && !props.isCollapsed && (
          <Children nodeId={props.id} />
        )}

        {props.isCollapsed && !props.isOverlay && (
          <NodePreviewWrapper nodeId={props.id} />
        )}
      </div>
    </div>
  )
}

export default NodeSimpleView
